import { computed, effect, inject, Injectable, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountService } from '@frontendlib/common/services/account.service';
import { LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { SubscriptionStatus } from '@sharedlib/common/types/accounts/subscription-status';
import { distinctUntilChanged, firstValueFrom, map, of, skip, switchMap, timeout, type Observable } from 'rxjs';
import {
  differs,
  isPendingCharge,
  recoveryReason,
  snapshotOf,
  type PendingCharge,
  type PendingChargeOrigin,
} from './pending-charge';
import { PaymentReceiptService } from './payment-receipt.service';

/** How long Billing reports a charge as under way before it says it is taking longer than usual. */
export const PENDING_CHARGE_LIFETIME_MS = 5 * 60 * 1000;

export const LANDED_CHECK_WAIT_MS = 3000;

const STORAGE_KEY = 'billing.pendingCharge';

/**
 * A charge the provider collects asynchronously, which Billing reports until the account moves off
 * {@link PendingCharge.snapshot}. Kept in `sessionStorage` per account, so it survives the redirect and a reload.
 */
@Injectable({ providedIn: 'root' })
export class PendingChargeService {
  private readonly accountService = inject(AccountService);
  private readonly receipts = inject(PaymentReceiptService);

  // Keyed by an explicit account id rather than the identity copy, so the marker follows the account
  // this service has loaded.
  private readonly stored = new LocalStorageItem<PendingCharge | null>(STORAGE_KEY, {
    storage: 'session',
    perAccount: false,
    parse: ({ raw }) => (isPendingCharge(raw) ? raw : null),
  });

  private readonly account = toSignal(this.accountService.getAccount(), { initialValue: null });

  private readonly marker = toSignal(
    this.accountService.getAccount().pipe(
      map((account) => account.id),
      distinctUntilChanged(),
      switchMap((accountId) => (accountId ? this.watch(accountId) : of(null))),
    ),
    { initialValue: null },
  );

  /** The account has moved off the marker's snapshot, i.e. the charge has landed. */
  public readonly settled = computed(() => {
    const account = this.account();
    const marker = this.marker();

    return account !== null && marker !== null && differs(account, marker.snapshot);
  });

  /** The current account's charge while it is still outstanding. */
  public readonly pending = computed(() => (this.settled() ? null : this.marker()));

  constructor() {
    effect(() => {
      if (this.settled()) {
        untracked(() => this.settle());
      }
    });
  }

  /** Clears a marker the account has moved off, recording the payment when it left the account active. */
  // A refusal or the timeout leaves the account where it was, so neither reaches here.
  public settle(): void {
    const marker = this.marker();
    const account = this.account();
    if (!marker || !this.settled()) {
      return;
    }
    this.clear();
    if (account?.subscriptionStatus !== SubscriptionStatus.ACTIVE) {
      return;
    }
    const since = new Date(marker.expiresAt - PENDING_CHARGE_LIFETIME_MS).toISOString();
    void (marker.reason === 'trial_upgrade'
      ? this.receipts.recordLatest(since, 'upgrade', marker.planTitle ? { planTitle: marker.planTitle } : {})
      : this.receipts.recordLatest(since, 'payment'));
  }

  public capture(): PendingChargeOrigin | null {
    const account = this.account();
    if (!account?.id) {
      return null;
    }

    return {
      accountId: account.id,
      status: account.subscriptionStatus,
      subscriptionPlanId: account.subscriptionPlanId ?? null,
    };
  }

  /** Starts a marker when `origin` is a state a newly billed card collects from; answers whether it did. */
  public startRecovery(origin: PendingChargeOrigin | null): boolean {
    const reason = recoveryReason(origin?.status);
    if (!origin || !reason) {
      return false;
    }

    return this.write(origin, {
      reason,
      snapshot: snapshotOf(origin),
      expiresAt: Date.now() + PENDING_CHARGE_LIFETIME_MS,
    });
  }

  public startUpgrade(origin: PendingChargeOrigin | null, planTitle: string): boolean {
    if (!origin) {
      return false;
    }

    return this.write(origin, {
      reason: 'trial_upgrade',
      planTitle,
      snapshot: snapshotOf(origin),
      expiresAt: Date.now() + PENDING_CHARGE_LIFETIME_MS,
    });
  }

  /** Re-reads the account and answers whether it has moved off `origin`. */
  public async hasLandedSince(origin: PendingChargeOrigin | null): Promise<boolean> {
    if (!origin) {
      return false;
    }
    const current = this.account();
    if (current && differs(current, snapshotOf(origin))) {
      return true;
    }
    // `skip(1)` drops the cached account the subject replays.
    const reloaded = firstValueFrom(
      this.accountService.getAccount().pipe(skip(1), timeout({ first: LANDED_CHECK_WAIT_MS, with: () => of(null) })),
      { defaultValue: null },
    );
    this.accountService.reloadAccount();
    const account = await reloaded;

    return account !== null && differs(account, snapshotOf(origin));
  }

  /** Keeps the marker, flagged as overdue, until the account moves or the session ends. */
  public expire(): void {
    const marker = this.marker();
    const origin = this.capture();
    if (marker && origin) {
      this.write(origin, { ...marker, expired: true });
    }
  }

  public clear(): void {
    const accountId = this.account()?.id;
    if (!accountId) {
      return;
    }
    try {
      this.stored.set(null, accountId);
    } catch (error: unknown) {
      console.warn('[PendingChargeService] Could not clear the pending charge', error);
    }
  }

  private watch(accountId: string): Observable<PendingCharge | null> {
    try {
      return this.stored.get(accountId).pipe(map((value) => value ?? null));
    } catch (error: unknown) {
      console.warn('[PendingChargeService] Could not read the pending charge', error);
      return of(null);
    }
  }

  private write(origin: PendingChargeOrigin, marker: PendingCharge): boolean {
    try {
      this.stored.set(marker, origin.accountId);
      return true;
    } catch (error: unknown) {
      console.warn('[PendingChargeService] Could not store the pending charge', error);
      return false;
    }
  }
}
